// Validation rules shared by the admin editor and the publish gate.
// A flow MUST pass validation before it can be published.

import { RxFlow, RxFlowNode } from '@/types/personalRxFlow';

export type RxValidationSeverity = 'error' | 'warning';

export interface RxValidationIssue {
  severity: RxValidationSeverity;
  code: string;
  message: string;
  nodeId?: string;
  optionId?: string;
}

export interface RxValidationReport {
  ok: boolean;
  errors: RxValidationIssue[];
  warnings: RxValidationIssue[];
  unreachableNodeIds: string[];
}

export function validateFlow(flow: RxFlow): RxValidationReport {
  const issues: RxValidationIssue[] = [];
  const nodeById = new Map(flow.nodes.map((n) => [n.id, n]));

  const startNodes = flow.nodes.filter((n) => n.type === 'start');
  if (startNodes.length !== 1) {
    issues.push({
      severity: 'error',
      code: 'start_count',
      message: `Flow must have exactly one start node (found ${startNodes.length}).`,
    });
  }
  if (!flow.nodes.some((n) => n.type === 'contact_form')) {
    issues.push({
      severity: 'error',
      code: 'missing_contact_form',
      message: 'Flow is missing a contact_form node.',
    });
  }
  if (!flow.nodes.some((n) => n.type === 'success')) {
    issues.push({
      severity: 'error',
      code: 'missing_success',
      message: 'Flow is missing a success node.',
    });
  }

  // Unique keys
  const seenKeys = new Map<string, string>();
  for (const n of flow.nodes) {
    const prev = seenKeys.get(n.key);
    if (prev) {
      issues.push({
        severity: 'error',
        code: 'duplicate_key',
        message: `Duplicate node key "${n.key}".`,
        nodeId: n.id,
      });
    }
    seenKeys.set(n.key, n.id);
  }

  // Option transitions
  for (const n of flow.nodes) {
    if (n.type === 'success') continue;
    if (n.type !== 'start' && n.options.length === 0 && n.type !== 'contact_form') {
      issues.push({
        severity: 'error',
        code: 'no_options',
        message: `Node "${n.key}" has no options/transition.`,
        nodeId: n.id,
      });
    }
    const seenOptKeys = new Set<string>();
    for (const o of n.options) {
      if (seenOptKeys.has(o.optionKey)) {
        issues.push({
          severity: 'error',
          code: 'duplicate_option_key',
          message: `Duplicate option_key "${o.optionKey}" on node "${n.key}".`,
          nodeId: n.id,
          optionId: o.id,
        });
      }
      seenOptKeys.add(o.optionKey);
      if (o.nextType === 'question') {
        if (!o.nextNodeId) {
          issues.push({
            severity: 'error',
            code: 'missing_target',
            message: `Option "${o.label}" of "${n.key}" has nextType=question but no target.`,
            nodeId: n.id,
            optionId: o.id,
          });
        } else if (!nodeById.has(o.nextNodeId)) {
          issues.push({
            severity: 'error',
            code: 'broken_reference',
            message: `Option "${o.label}" of "${n.key}" points at a missing node.`,
            nodeId: n.id,
            optionId: o.id,
          });
        }
      }
    }
  }

  // Reachability + cycle detection (DFS over option edges).
  const startId = startNodes[0]?.id ?? flow.startNodeId;
  const visited = new Set<string>();
  const stack: string[] = startId ? [startId] : [];
  while (stack.length) {
    const id = stack.pop()!;
    if (visited.has(id)) continue;
    visited.add(id);
    const node = nodeById.get(id);
    if (!node) continue;
    for (const o of node.options) {
      if (o.nextType === 'question' && o.nextNodeId) stack.push(o.nextNodeId);
      if (o.nextType === 'contact_form' && flow.contactFormNodeId) stack.push(flow.contactFormNodeId);
      if (o.nextType === 'success' && flow.successNodeId) stack.push(flow.successNodeId);
      // continue / end_category resolve dynamically at runtime — not used for static reachability.
    }
  }
  const unreachable: string[] = [];
  for (const n of flow.nodes) {
    if (n.type === 'start') continue;
    // Continue-reachable nodes (sibling questions in same category) count as reachable too.
    if (visited.has(n.id)) continue;
    if (n.type === 'question' && n.category) {
      // a sibling in the same category that was visited makes this one runtime-reachable
      const sibling = flow.nodes.find(
        (s) => s.category === n.category && s.type === 'question' && visited.has(s.id),
      );
      if (sibling) continue;
    }
    unreachable.push(n.id);
    issues.push({
      severity: 'warning',
      code: 'unreachable',
      message: `Node "${n.key}" is not reachable from start.`,
      nodeId: n.id,
    });
  }

  const errors = issues.filter((i) => i.severity === 'error');
  const warnings = issues.filter((i) => i.severity === 'warning');
  return {
    ok: errors.length === 0,
    errors,
    warnings,
    unreachableNodeIds: unreachable,
  };
}
